import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/Motion";
import { Search, SlidersHorizontal, Loader2, CarFront, RotateCcw } from "lucide-react";

const BODY = ["Hatchback", "Saloon", "Estate", "SUV", "Coupe", "Convertible", "MPV", "Pickup", "Van"];
const FUEL = ["Petrol", "Diesel", "Electric", "Hybrid"];
const COLOURS = ["Black", "White", "Silver", "Grey", "Blue", "Red", "Green", "Other"];
const EMPTY = { make: "", model: "", min_price: "", max_price: "", min_mileage: "", max_mileage: "", seller_type: "", postcode: "", radius: "", max_age: "", min_power: "", max_power: "", body_type: "", fuel: "", transmission: "", colour: "", verified: "", sort: "newest" };
const KEYS = Object.keys(EMPTY);

function Pick({ label, value, onChange, options, testId, placeholder = "Any" }) {
  return (
    <div>
      <span className="block text-[11px] uppercase tracking-widest font-semibold text-slatev mb-1.5">{label}</span>
      <Select value={value || "any"} onValueChange={(v) => onChange(v === "any" ? "" : v)}>
        <SelectTrigger className="bg-white h-10" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-white"><SelectItem value="any">{placeholder}</SelectItem>{options.map((o) => {
          const [v, l] = Array.isArray(o) ? o : [o, o];
          return <SelectItem key={v} value={String(v)}>{l}</SelectItem>;
        })}</SelectContent>
      </Select>
    </div>
  );
}

function Range({ label, a, b, onA, onB, testId, ph = ["Min", "Max"] }) {
  return (
    <div>
      <span className="block text-[11px] uppercase tracking-widest font-semibold text-slatev mb-1.5">{label}</span>
      <div className="grid grid-cols-2 gap-2">
        <Input type="number" placeholder={ph[0]} value={a} onChange={(e) => onA(e.target.value)} className="bg-white h-10" data-testid={`${testId}-min`} />
        <Input type="number" placeholder={ph[1]} value={b} onChange={(e) => onB(e.target.value)} className="bg-white h-10" data-testid={`${testId}-max`} />
      </div>
    </div>
  );
}

function FilterPanel({ f, upd, meta, reset }) {
  return (
    <div className="space-y-5" data-testid="filter-panel">
      <Pick label="Make" value={f.make} onChange={(v) => { upd("make", v); upd("model", ""); }} options={meta.makes} testId="filter-make" placeholder="Any make" />
      <Pick label="Model" value={f.model} onChange={(v) => upd("model", v)} options={meta.models} testId="filter-model" placeholder={f.make ? "Any model" : "Pick a make first"} />
      <Range label="Price (£)" a={f.min_price} b={f.max_price} onA={(v) => upd("min_price", v)} onB={(v) => upd("max_price", v)} testId="filter-price" />
      <Range label="Mileage" a={f.min_mileage} b={f.max_mileage} onA={(v) => upd("min_mileage", v)} onB={(v) => upd("max_mileage", v)} testId="filter-mileage" />
      <Pick label="Type of listing" value={f.seller_type} onChange={(v) => upd("seller_type", v)} options={[["private", "Private sellers"], ["dealer", "Dealers"]]} testId="filter-seller-type" placeholder="All sellers" />
      <div>
        <span className="block text-[11px] uppercase tracking-widest font-semibold text-slatev mb-1.5">Distance</span>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Postcode" value={f.postcode} onChange={(e) => upd("postcode", e.target.value.toUpperCase())} className="bg-white h-10" data-testid="filter-postcode" />
          <Select value={f.radius || "any"} onValueChange={(v) => upd("radius", v === "any" ? "" : v)}>
            <SelectTrigger className="bg-white h-10" data-testid="filter-radius"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="any">National</SelectItem>{[5, 10, 25, 50, 100, 200].map((r) => <SelectItem key={r} value={String(r)}>Within {r} mi</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Pick label="Age" value={f.max_age} onChange={(v) => upd("max_age", v)} options={[1, 2, 3, 5, 7, 10, 15].map((y) => [y, `Up to ${y} year${y > 1 ? "s" : ""} old`])} testId="filter-age" placeholder="Any age" />
      <Range label="Power (bhp)" a={f.min_power} b={f.max_power} onA={(v) => upd("min_power", v)} onB={(v) => upd("max_power", v)} testId="filter-power" />
      <Pick label="Car type" value={f.body_type} onChange={(v) => upd("body_type", v)} options={[...new Set([...BODY, ...meta.body_types])]} testId="filter-body-type" />
      <Pick label="Fuel" value={f.fuel} onChange={(v) => upd("fuel", v)} options={FUEL} testId="filter-fuel" />
      <Pick label="Gearbox" value={f.transmission} onChange={(v) => upd("transmission", v)} options={["Manual", "Automatic"]} testId="filter-transmission" />
      <Pick label="Colour" value={f.colour} onChange={(v) => upd("colour", v)} options={[...new Set([...COLOURS, ...meta.colours])]} testId="filter-colour" />
      <label className="flex items-center gap-2 text-sm text-navy"><Checkbox checked={!!f.verified} onCheckedChange={(v) => upd("verified", v ? "true" : "")} data-testid="filter-verified" className="data-[state=checked]:bg-teal data-[state=checked]:border-teal" />Verified only</label>
      <button onClick={reset} data-testid="filter-reset" className="text-sm font-semibold text-teal inline-flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" />Clear all filters</button>
    </div>
  );
}

function useListings(f, q) {
  const [state, setState] = useState({ items: [], loading: true, meta: { makes: [], models: [], colours: [], body_types: [] } });
  useEffect(() => {
    const t = setTimeout(async () => {
      setState((s) => ({ ...s, loading: true }));
      const p = { ...(q ? { q } : {}) };
      KEYS.forEach((k) => { if (f[k]) p[k] = f[k]; });
      if (p.postcode && !/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(p.postcode.trim())) { delete p.postcode; }
      if (!p.postcode) { delete p.radius; if (p.sort === "nearest") delete p.sort; }
      try {
        const { data } = await api.get("/listings", { params: p });
        setState({ items: data.items, loading: false, meta: { makes: data.makes, models: data.models, colours: data.colours, body_types: data.body_types } });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        setState((s) => ({ ...s, loading: false }));
      }
    }, 350);
    return () => clearTimeout(t);
  }, [f, q]);
  return state;
}

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [query, setQuery] = useState(params.get("q") || "");
  const [f, setF] = useState(() => {
    const init = { ...EMPTY };
    KEYS.forEach((k) => { if (params.get(k)) init[k] = params.get(k); });
    if (params.get("verified")) init.verified = "true";
    return init;
  });
  const { items, loading, meta } = useListings(f, query);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v, ...(k === "postcode" && !v && s.sort === "nearest" ? { sort: "newest" } : {}) }));
  const submit = (e) => { e.preventDefault(); setQuery(q); setParams(q ? { q } : {}); };
  const active = KEYS.filter((k) => k !== "sort" && f[k]).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="browse-page">
      <Reveal>
        <span className="text-xs uppercase tracking-widest font-semibold text-teal">Browse</span>
        <h1 className="font-heading font-extrabold text-navy tracking-tighter text-4xl sm:text-5xl mt-2">Find your next car.</h1>
      </Reveal>
      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <form onSubmit={submit} className="flex gap-2 flex-1 min-w-[260px] max-w-xl">
          <div className="relative flex-1"><Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="browse-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search make, model…" className="pl-10 h-12 rounded-full bg-white" /></div>
          <Button type="submit" className="h-12 px-6 rounded-full bg-teal hover:bg-teal-dark text-white" data-testid="browse-search-btn">Search</Button>
        </form>
        <Sheet>
          <SheetTrigger asChild><Button variant="outline" className="lg:hidden h-12 rounded-full" data-testid="filters-mobile-btn"><SlidersHorizontal className="w-4 h-4 mr-2" />Filters{active ? ` (${active})` : ""}</Button></SheetTrigger>
          <SheetContent side="left" className="bg-background overflow-y-auto w-[320px]"><SheetTitle className="font-heading mb-5">Filters</SheetTitle><FilterPanel f={f} upd={upd} meta={meta} reset={() => setF(EMPTY)} /></SheetContent>
        </Sheet>
        <div className="ml-auto w-[190px]">
          <Select value={f.sort} onValueChange={(v) => upd("sort", v)}><SelectTrigger className="bg-white h-12 rounded-full" data-testid="filter-sort"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="newest">Newest</SelectItem><SelectItem value="price_low">Price: low to high</SelectItem><SelectItem value="price_high">Price: high to low</SelectItem><SelectItem value="mileage_low">Lowest mileage</SelectItem>{f.postcode && <SelectItem value="nearest">Nearest first</SelectItem>}</SelectContent></Select>
        </div>
      </div>
      <div className="mt-8 grid lg:grid-cols-[280px_1fr] gap-8 items-start">
        <aside className="hidden lg:block bg-white rounded-3xl border border-navy/10 p-6 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto"><FilterPanel f={f} upd={upd} meta={meta} reset={() => setF(EMPTY)} /></aside>
        <div>
          {loading ? <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>
            : items.length === 0 ? (
              <div className="text-center py-24 text-slatev" data-testid="browse-empty"><CarFront className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>No cars match your filters yet.</p></div>
            ) : (
              <>
                <p className="text-sm text-slatev mb-4" data-testid="browse-count">{items.length} car{items.length !== 1 ? "s" : ""} found</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">{items.map((l) => <ListingCard key={l.id} listing={l} />)}</div>
              </>
            )}
        </div>
      </div>
    </div>
  );
}
